import { CheckCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Divider, Drawer, Flex, Form, Input, InputNumber, Select, Table, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@nks/shared';
import { ListPage } from '../../components/ListPage';
import { useHasPermission } from '../auth/session';
import { SaleReviews } from './SaleReviews';
import { SuggestedGroups } from './SuggestedGroups';
import {
  createBikeModel,
  fetchExistingMappings,
  fetchModelOptions,
  fetchUnmappedCodes,
  mapItemCode,
  remapItemCode,
  type ExistingMapping,
  type UnmappedGroup,
} from './api';

const { Text } = Typography;

/**
 * Declaring which AutoCount item code is which bike model (Q85).
 *
 * This screen exists because `motorcycle_units.model_id` is required and their
 * catalogue cannot be parsed into one: the same bike carries several codes, and
 * `KTM-DUKE200-ABS` versus `KTM-200 DUKE` may be one model keyed twice or a
 * genuine ABS and non-ABS pair. Only NKS knows. Nothing here guesses.
 *
 * The work is deliberately ordered heaviest-first. 246 codes sounds like a
 * chore; the top ten cover about two thirds of the fleet, and the very first
 * one releases 758 motorcycles. Someone who sees that first will finish the
 * job — someone handed an alphabetical list will not.
 */
export function ModelMapPage(): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const canManage = useHasPermission(PERMISSIONS.MODEL_MAP_MANAGE);
  const [choice, setChoice] = useState<Record<string, string | undefined>>({});
  // Which row opened the create drawer. The new model is selected straight
  // into that row afterwards, so the person is never left to find it again in
  // a list of hundreds.
  const [creatingFor, setCreatingFor] = useState<UnmappedGroup | null>(null);
  // Which settled match is being corrected. Held separately from `creatingFor`
  // so the two drawers cannot both think they are open.
  const [correcting, setCorrecting] = useState<ExistingMapping | null>(null);
  const [correctForm] = Form.useForm<{ modelUid: string; reason: string }>();
  const [form] = Form.useForm<{
    brandName: string;
    model: string;
    variant?: string;
    modelYear?: number;
  }>();

  const unmapped = useQuery({ queryKey: ['model-map', 'unmapped'], queryFn: fetchUnmappedCodes });
  const models = useQuery({ queryKey: ['model-map', 'models'], queryFn: fetchModelOptions });
  const existing = useQuery({ queryKey: ['model-map', 'existing'], queryFn: fetchExistingMappings });

  const mutation = useMutation({
    mutationFn: ({ itemCode, modelUid }: { itemCode: string; modelUid: string }) =>
      mapItemCode(itemCode, modelUid),
    onSuccess: (result, variables) => {
      // Say what actually happened, in the units the user cares about. "Mapped
      // successfully" is true and useless; "758 motorcycles added" is the thing
      // they were trying to achieve.
      message.success(
        t('vehicles.modelMap.mapped', {
          count: result.unitsCreated,
          model: result.mapped,
          code: variables.itemCode,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ['model-map'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
    },
    onError: () => {
      message.error(t('vehicles.modelMap.failed'));
    },
  });

  const remap = useMutation({
    mutationFn: remapItemCode,
    onSuccess: (result) => {
      message.success(
        t('vehicles.modelMap.remapped', {
          count: result.unitsChanged,
          from: result.from,
          to: result.to,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: ['model-map'] });
      void queryClient.invalidateQueries({ queryKey: ['units'] });
      setCorrecting(null);
      correctForm.resetFields();
    },
    onError: (error: Error) => {
      // The server's message names the blocking chassis numbers, which is the
      // whole point of it — showing a generic failure would throw that away.
      message.error(error.message);
    },
  });

  const createModel = useMutation({
    mutationFn: createBikeModel,
    onSuccess: (model) => {
      const code = creatingFor?.itemCode;
      message.success(t('vehicles.modelMap.modelCreated', { model: model.label }));
      void queryClient.invalidateQueries({ queryKey: ['model-map', 'models'] });
      // Pre-select it for the row that asked for it.
      if (code !== undefined) setChoice((prev) => ({ ...prev, [code]: model.uid }));
      setCreatingFor(null);
      form.resetFields();
    },
    onError: () => {
      message.error(t('vehicles.modelMap.modelFailed'));
    },
  });

  const rows = unmapped.data ?? [];
  const waiting = rows.reduce((sum, r) => sum + r.unitCount, 0);

  return (
    <Flex vertical gap={16}>
      <Card size="small">
        <Flex vertical gap={4}>
          <Text strong>{t('vehicles.modelMap.title')}</Text>
          <Text type="secondary">
            {t('vehicles.modelMap.intro', { units: waiting, codes: rows.length })}
          </Text>
        </Flex>
      </Card>

      {rows.length > 0 && <SuggestedGroups canManage={canManage} />}

      <SaleReviews canManage={canManage} />

      <ListPage<UnmappedGroup>
        title={t('vehicles.modelMap.queue')}
        loading={unmapped.isLoading}
        emptyDescription={t('vehicles.modelMap.allDone')}
        rows={rows}
        rowKey={(row) => row.itemCode}
        columns={[
          {
            title: t('vehicles.modelMap.itemCode'),
            dataIndex: 'itemCode',
            width: 240,
            render: (code: string) => <span className="tabular">{code}</span>,
          },
          {
            title: t('vehicles.modelMap.units'),
            dataIndex: 'unitCount',
            width: 100,
            align: 'right' as const,
            render: (n: number) => <span className="tabular">{n}</span>,
          },
          {
            // Colour and year are what tell a person which bike this is when
            // the code alone is ambiguous — exactly the ABS/non-ABS case.
            title: t('vehicles.modelMap.seen'),
            width: 260,
            render: (_: unknown, row: UnmappedGroup) => (
              <Flex gap={4} wrap>
                {row.colours.slice(0, 3).map((c) => (
                  <Tag key={c}>{c}</Tag>
                ))}
                {row.modelYears.slice(0, 3).map((y) => (
                  <Tag key={y}>{y}</Tag>
                ))}
              </Flex>
            ),
          },
          {
            title: t('vehicles.modelMap.sample'),
            width: 200,
            render: (_: unknown, row: UnmappedGroup) => (
              <Text type="secondary" className="tabular" style={{ fontSize: 12 }}>
                {row.sampleChassis[0] ?? '—'}
              </Text>
            ),
          },
          {
            title: t('vehicles.modelMap.mapTo'),
            render: (_: unknown, row: UnmappedGroup) => (
              <Flex gap={8}>
                <Select
                  showSearch
                  style={{ minWidth: 240 }}
                  placeholder={t('vehicles.modelMap.choose')}
                  optionFilterProp="label"
                  loading={models.isLoading}
                  popupRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <Button
                        type="text"
                        block
                        icon={<PlusOutlined />}
                        onClick={() => {
                          // Seed only the year, which is a fact AutoCount
                          // actually holds. The model name is deliberately
                          // left empty: pre-filling it with the item code
                          // invites somebody to accept "KTM-DUKE200-ABS" as a
                          // model NAME, which bakes the catalogue
                          // fragmentation into the thing built to resolve it.
                          // The code and its evidence are on screen in the
                          // drawer, so nothing is lost by making them type.
                          form.setFieldsValue({
                            modelYear: Number(row.modelYears.find((y) => /^\d{4}$/.test(y))) || undefined,
                          });
                          setCreatingFor(row);
                        }}
                      >
                        {t('vehicles.modelMap.createModel')}
                      </Button>
                    </>
                  )}
                  value={choice[row.itemCode]}
                  onChange={(value: string) => {
                    setChoice((prev) => ({ ...prev, [row.itemCode]: value }));
                  }}
                  options={(models.data ?? []).map((m) => ({ value: m.uid, label: m.label }))}
                />
                <Button
                  type="primary"
                  disabled={choice[row.itemCode] === undefined}
                  loading={mutation.isPending && mutation.variables.itemCode === row.itemCode}
                  onClick={() => {
                    const modelUid = choice[row.itemCode];
                    if (modelUid === undefined) return;
                    mutation.mutate({ itemCode: row.itemCode, modelUid });
                  }}
                >
                  {/* Names the outcome, not the mechanism (CLAUDE.md §10). */}
                  {t('vehicles.modelMap.action', { count: row.unitCount })}
                </Button>
              </Flex>
            ),
          },
        ]}
        renderMobileRow={(row) => (
          <Flex vertical gap={6}>
            <Flex justify="space-between" align="center">
              <Text strong className="tabular">
                {row.itemCode}
              </Text>
              <Tag>{t('vehicles.modelMap.unitsShort', { count: row.unitCount })}</Tag>
            </Flex>
            <Select
              showSearch
              size="large"
              placeholder={t('vehicles.modelMap.choose')}
              optionFilterProp="label"
              value={choice[row.itemCode]}
              onChange={(value: string) => {
                setChoice((prev) => ({ ...prev, [row.itemCode]: value }));
              }}
              options={(models.data ?? []).map((m) => ({ value: m.uid, label: m.label }))}
            />
            <Button
              type="primary"
              size="large"
              block
              disabled={choice[row.itemCode] === undefined}
              onClick={() => {
                const modelUid = choice[row.itemCode];
                if (modelUid === undefined) return;
                mutation.mutate({ itemCode: row.itemCode, modelUid });
              }}
            >
              {t('vehicles.modelMap.action', { count: row.unitCount })}
            </Button>
          </Flex>
        )}
      />

      <Drawer
        className="nks-action-drawer"
        width={420}
        open={creatingFor !== null}
        title={t('vehicles.modelMap.createTitle')}
        onClose={() => {
          setCreatingFor(null);
          form.resetFields();
        }}
        extra={
          <Button
            type="primary"
            loading={createModel.isPending}
            onClick={() => {
              void form.validateFields().then((values) => {
                createModel.mutate(values);
              });
            }}
          >
            {t('vehicles.modelMap.createAction')}
          </Button>
        }
      >
        {creatingFor !== null && (
          <Flex vertical gap={12}>
            {/* The evidence stays on screen. Deciding what a code is without
                seeing its colours and years is guesswork. */}
            <Card size="small">
              <Flex vertical gap={4}>
                <Text strong className="tabular">
                  {creatingFor.itemCode}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('vehicles.modelMap.evidence', {
                    count: creatingFor.unitCount,
                    colours: creatingFor.colours.slice(0, 4).join(', ') || '—',
                    years: creatingFor.modelYears.slice(0, 4).join(', ') || '—',
                  })}
                </Text>
              </Flex>
            </Card>

            <Form form={form} layout="vertical">
              <Form.Item
                name="brandName"
                label={t('vehicles.modelMap.brand')}
                rules={[{ required: true }]}
              >
                <Input size="large" autoFocus placeholder="KTM" />
              </Form.Item>
              <Form.Item
                name="model"
                label={t('vehicles.modelMap.modelName')}
                rules={[{ required: true }]}
                extra={t('vehicles.modelMap.modelNameHint')}
              >
                <Input size="large" placeholder="200 Duke" />
              </Form.Item>
              <Form.Item name="variant" label={t('vehicles.modelMap.variant')}>
                <Input size="large" placeholder="ABS" />
              </Form.Item>
              <Form.Item name="modelYear" label={t('vehicles.modelMap.year')}>
                <InputNumber size="large" style={{ width: '100%' }} min={1900} max={2100} />
              </Form.Item>
            </Form>
          </Flex>
        )}
      </Drawer>

      {(existing.data ?? []).length > 0 && (
        <Card
          size="small"
          title={
            <Flex align="center" gap={8}>
              <CheckCircleOutlined />
              {t('vehicles.modelMap.settled', { count: existing.data?.length ?? 0 })}
            </Flex>
          }
        >
          <Table
            size="small"
            pagination={false}
            rowKey={(row) => row.itemCode}
            dataSource={existing.data ?? []}
            columns={[
              {
                title: t('vehicles.modelMap.itemCode'),
                dataIndex: 'itemCode',
                render: (code: string) => <span className="tabular">{code}</span>,
              },
              { title: t('vehicles.modelMap.model'), dataIndex: 'model' },
              {
                title: t('vehicles.modelMap.units'),
                dataIndex: 'unitCount',
                width: 120,
                align: 'right' as const,
                render: (n: number) => <span className="tabular">{n}</span>,
              },
              {
                width: 120,
                align: 'right' as const,
                render: (_: unknown, row: ExistingMapping) => (
                  <Button
                    size="small"
                    onClick={() => {
                      correctForm.setFieldsValue({ modelUid: row.modelUid, reason: '' });
                      setCorrecting(row);
                    }}
                  >
                    {t('vehicles.modelMap.correct')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Drawer
        className="nks-action-drawer"
        width={420}
        open={correcting !== null}
        title={t('vehicles.modelMap.correctTitle')}
        onClose={() => {
          setCorrecting(null);
          correctForm.resetFields();
        }}
        extra={
          <Button
            type="primary"
            danger
            loading={remap.isPending}
            onClick={() => {
              void correctForm.validateFields().then((values) => {
                if (correcting === null) return;
                remap.mutate({ itemCode: correcting.itemCode, ...values });
              });
            }}
          >
            {t('vehicles.modelMap.correctAction')}
          </Button>
        }
      >
        {correcting !== null && (
          <Flex vertical gap={12}>
            {/* Say what this will change before it changes it (CLAUDE.md §10).
                The count is the part that matters: correcting three bikes and
                correcting 758 are very different acts. */}
            <Card size="small">
              <Flex vertical gap={4}>
                <Text strong className="tabular">
                  {correcting.itemCode}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('vehicles.modelMap.correctWarning', {
                    count: correcting.unitCount,
                    model: correcting.model,
                  })}
                </Text>
              </Flex>
            </Card>

            <Form form={correctForm} layout="vertical">
              <Form.Item
                name="modelUid"
                label={t('vehicles.modelMap.correctTo')}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  size="large"
                  optionFilterProp="label"
                  placeholder={t('vehicles.modelMap.choose')}
                  options={(models.data ?? []).map((m) => ({ value: m.uid, label: m.label }))}
                />
              </Form.Item>
              <Form.Item
                name="reason"
                label={t('vehicles.modelMap.reason')}
                rules={[{ required: true, min: 10 }]}
                extra={t('vehicles.modelMap.reasonHint')}
              >
                <Input.TextArea rows={3} size="large" />
              </Form.Item>
            </Form>
          </Flex>
        )}
      </Drawer>
    </Flex>
  );
}
